// @ts-nocheck
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconProps } from "@insite/mobius/Icon";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
} from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { HeadingPresentationProps } from "@store/Library/Components/Toolkit/Heading";
import AlertCircleFill from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleFill";
import AlertTriangleFill from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleFill";
import CheckmarkCircle2Fill from "@store/Library/Components/Toolkit/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import constants from "@store/constants";
import loadTaxExemptionCertificates from "@store/Framework/Store/Greif/Data/TaxExemptionCertificates/Handlers/LoadTaxExemptionCertificates";
import { getTaxExemptionCertificatesDataView } from "@store/Framework/Store/Greif/Data/TaxExemptionCertificates/TaxExemptionCertificatesSelectors";
import loadTECMessages from "@store/Framework/Store/Greif/Data/TECMessages/Handlers/LoadTECMessages";
import { getTECMessagesDataView } from "@store/Framework/Store/Greif/Data/TECMessages/TECMessagesSelectors";
import TECContactSupportPageTypeLink from "@store/Library/Components/TECContactSupportPageTypeLink";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { accountMessagesStyles } from "@store/Overrides/Widgets/MyAccount/AccountMessages";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    presentation = "presentation",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.presentation]: string;
        [fields.customCSS]: string;
    };
}

const tecMessagesParameter = {};

const mapStateToProps = (state: ApplicationState) => {
    const { getTaxExemptionCertificatesParameter } = state.greif.pages.taxExemptionCertificates;

    return {
        getTaxExemptionCertificatesParameter,
        tecMessagesDataView: getTECMessagesDataView(state, tecMessagesParameter),
        tecPageLink: getPageLinkByPageType(state, constants.pageType.taxExemptionCertificates),
        myAccountPage: getPageLinkByPageType(state, "MyAccountPage"),
        taxExemptionCertificatesDataView: getTaxExemptionCertificatesDataView(
            state,
            getTaxExemptionCertificatesParameter,
        ),
    };
};

const mapDispatchToProps = {
    loadTECMessages,
    loadTaxExemptionCertificates,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface IconStatusProps {
    expired?: IconProps;
    expiringSoon?: IconProps;
    allValid?: IconProps;
}

interface TECMessageStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    noExpiringCertFoundText?: TypographyPresentationProps;
    article?: InjectableCss;
    heading?: HeadingPresentationProps;
    bodyText?: TypographyProps;
    actionLink?: LinkPresentationProps;
    bodyTextFlexWrapper?: FlexWrapperPresentationProps;
}

const tecMessagesStyles: TECMessageStyles = {
    article: accountMessagesStyles.messageCard?.article,
    bodyTextFlexWrapper: {
        extendedStyles: {
            content: {
                css: css`
                    gap: ${pxToRem(16)};
                    align-items: center;
                `,
            },
        },
    },
    bodyText: accountMessagesStyles.messageCard?.bodyText,
    actionLink: accountMessagesStyles.messageCard?.actionLink,
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-expired-certificates-text{
}`;

const styles = tecMessagesStyles;

const StyledArticle = getStyledWrapper("article");

const AccountTECMessages = ({
    tecMessagesDataView,
    taxExemptionCertificatesDataView,
    getTaxExemptionCertificatesParameter,
    loadTECMessages,
    loadTaxExemptionCertificates,
    tecPageLink,
    myAccountPage,
    fields,
}: Props) => {
    useEffect(() => {
        if (!tecMessagesDataView.value && !tecMessagesDataView.isLoading) {
            loadTECMessages(tecMessagesParameter);
        }
    }, [tecMessagesDataView]);

    useEffect(() => {
        if (!taxExemptionCertificatesDataView.value && !taxExemptionCertificatesDataView.isLoading) {
            loadTaxExemptionCertificates(getTaxExemptionCertificatesParameter);
        }
    }, [taxExemptionCertificatesDataView]);

    const hasTaxExemptionCertificates = taxExemptionCertificatesDataView.value?.length || 0;

    const customCssWrapper = css`
        ${styles.wrapper?.css}
        ${fields.customCSS}
    `;

    const iconSize = 24;

    const statusIconProps: IconStatusProps = {
        expired: {
            color: "danger",
            src: AlertCircleFill,
            size: iconSize,
        },
        expiringSoon: {
            color: "warning",
            src: AlertTriangleFill,
            size: iconSize,
        },
        allValid: {
            color: "success",
            src: CheckmarkCircle2Fill,
            size: iconSize,
        },
    };

    const mapStatusToIcon = (status?: string) => {
        switch (status) {
            case "Expired":
                return statusIconProps.expired;
            case "allValid":
                return statusIconProps.allValid;
            default:
                return statusIconProps.expiringSoon;
        }
    };

    const displayAsCard: boolean = fields.presentation === "cards";

    const messagesCount = tecMessagesDataView.value?.length;
    const headingText = messagesCount
        ? translate("Tax Exemption Certificates ({0})", String(messagesCount))
        : translate("Tax Exemption Certificates");

    return (
        <StyledWrapper {...styles.wrapper} css={customCssWrapper} data-test-selector="widget_TaxExemptionCertificates">
            <CardListHeading
                heading={headingText}
                viewAllUrl={tecPageLink?.url}
                hasNoResults={tecMessagesDataView.value?.length === 0}
            />
            <DataViewLoadingOverlay {...styles.loadingOverlay} dataView={tecMessagesDataView}>
                {tecMessagesDataView.value?.length === 0 ? (
                    hasTaxExemptionCertificates ? (
                        <FlexWrapper {...styles.bodyTextFlexWrapper}>
                            <Icon {...mapStatusToIcon("allValid")} />
                            <Typography {...styles.bodyText}>
                                {translate("All your certificates are valid.")}
                            </Typography>
                        </FlexWrapper>
                    ) : (
                        <Typography className="no-expired-certificates-text" {...styles.noExpiringCertFoundText}>
                            {translate("No certificates.")}
                        </Typography>
                    )
                ) : (
                    <>
                        {displayAsCard ? (
                            tecMessagesDataView.value?.map(message => (
                                <StyledArticle {...styles.article} key={message.id}>
                                    <FlexWrapper {...styles.bodyTextFlexWrapper}>
                                        <Icon {...mapStatusToIcon(message.status)} size={24} />
                                        <Typography {...styles.bodyText}>{message.message}</Typography>
                                        <Typography {...styles.actionLink}>
                                            <TECContactSupportPageTypeLink
                                                title={translate("Renew")}
                                                certificateId={message.id}
                                                returnPage={myAccountPage}
                                            />
                                        </Typography>
                                    </FlexWrapper>
                                </StyledArticle>
                            ))
                        ) : (
                            <DataTable>
                                <DataTableHead>
                                    <DataTableHeader>{translate("Message")}</DataTableHeader>
                                    <DataTableHeader>{translate("Action")}</DataTableHeader>
                                </DataTableHead>
                                <DataTableBody>
                                    {tecMessagesDataView.value?.map(message => (
                                        <DataTableRow key={message.id}>
                                            <DataTableCell>
                                                <FlexWrapper {...styles.bodyTextFlexWrapper}>
                                                    <Icon {...mapStatusToIcon(message.status)} />
                                                    <Typography {...styles.bodyText}>{message.message}</Typography>
                                                </FlexWrapper>
                                            </DataTableCell>
                                            <DataTableCell>
                                                <TECContactSupportPageTypeLink
                                                    title={translate("Renew")}
                                                    certificateId={message.id}
                                                    returnPage={myAccountPage}
                                                />
                                            </DataTableCell>
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        )}
                    </>
                )}
            </DataViewLoadingOverlay>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountTECMessages),
    definition: {
        displayName: "TEC Notifications",
        group: "My Account",
        allowedContexts: ["MyAccountPage"],
        fieldDefinitions: [
            {
                name: fields.presentation,
                displayName: "Presentation",
                defaultValue: "cards",
                editorTemplate: "DropDownField",
                fieldType: "General",
                tab: basicTab,
                options: [
                    {
                        displayName: "Cards",
                        value: "cards",
                    },
                    {
                        displayName: "Table",
                        value: "table",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                tab: advancedTab,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
